/**
 * Admin Dashboard - Gallery & Testimonials Management
 */

import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Plus,
  Edit,
  Trash2,
  Image as ImageIcon,
  LogOut,
  Package,
  DollarSign,
  Tag,
  Loader2,
  AlertCircle,
  CheckCircle,
  Upload,
  X,
  MessageSquare,
  Star,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useAuthStore } from '@/stores/authStore';

interface GalleryItem {
  id: string;
  title: string;
  description: string | null;
  category: string;
  image_url: string | null;
  thumbnail_url: string | null;
  price: number | null;
  price_display: string;
  status: string;
  tags: string[];
  species: string | null;
  dimensions: string | null;
  sort_order: number;
  featured: boolean;
  created_at: string;
  updated_at: string;
}

interface Testimonial {
  id: string;
  name: string;
  location: string | null;
  service: string | null;
  text: string;
  rating: number;
  featured: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

type ActiveTab = 'gallery' | 'testimonials';
type FormMode = 'list' | 'create' | 'edit';

const categoryOptions = [
  { value: 'bird', label: 'Birds' },
  { value: 'mammal', label: 'Mammals' },
  { value: 'restoration', label: 'Restoration' },
  { value: 'other', label: 'Other' },
];

const statusOptions = [
  { value: 'available', label: 'Available' },
  { value: 'sold', label: 'Sold' },
  { value: 'reserved', label: 'Reserved' },
  { value: 'hidden', label: 'Hidden' },
];

const priceDisplayOptions = [
  { value: 'upon_request', label: 'Price Upon Request' },
  { value: 'fixed', label: 'Fixed Price' },
  { value: 'sold', label: 'Sold' },
  { value: 'not_for_sale', label: 'Not For Sale' },
];

const serviceOptions = [
  { value: 'gardening', label: 'Gardening' },
  { value: 'cleaning', label: 'Cleaning' },
  { value: 'taxidermy', label: 'Taxidermy' },
  { value: 'general', label: 'General' },
];

export function AdminDashboard() {
  const navigate = useNavigate();
  const { user, token, isAuthenticated, logout, checkAuth } = useAuthStore();

  const [activeTab, setActiveTab] = useState<ActiveTab>('gallery');
  const [items, setItems] = useState<GalleryItem[]>([]);
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [isLoadingItems, setIsLoadingItems] = useState(true);
  const [isLoadingTestimonials, setIsLoadingTestimonials] = useState(true);
  const [mode, setMode] = useState<FormMode>('list');
  const [editingItem, setEditingItem] = useState<GalleryItem | null>(null);
  const [editingTestimonial, setEditingTestimonial] = useState<Testimonial | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Gallery form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'bird',
    image_url: '',
    thumbnail_url: '',
    price: '',
    price_display: 'upon_request',
    status: 'available',
    species: '',
    dimensions: '',
    tags: '',
    sort_order: '0',
    featured: false,
  });

  // Testimonial form state
  const [testimonialForm, setTestimonialForm] = useState({
    name: '',
    location: '',
    service: 'general',
    text: '',
    rating: '5',
    featured: true,
    sort_order: '0',
  });

  // Check auth on mount
  useEffect(() => {
    checkAuth();
  }, []);

  // Redirect if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/admin');
    }
  }, [isAuthenticated, navigate]);

  // Load data
  useEffect(() => {
    if (token) {
      loadItems();
      loadTestimonials();
    }
  }, [token]);

  const loadItems = async () => {
    setIsLoadingItems(true);
    try {
      const response = await fetch('/api/gallery?includeHidden=true', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      setItems(data.items || []);
    } catch (error) {
      console.error('Failed to load items:', error);
    } finally {
      setIsLoadingItems(false);
    }
  };

  const loadTestimonials = async () => {
    setIsLoadingTestimonials(true);
    try {
      const response = await fetch('/api/testimonials?all=true', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      setTestimonials(data.testimonials || []);
    } catch (error) {
      console.error('Failed to load testimonials:', error);
    } finally {
      setIsLoadingTestimonials(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/admin');
  };

  // Gallery functions
  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      category: 'bird',
      image_url: '',
      thumbnail_url: '',
      price: '',
      price_display: 'upon_request',
      status: 'available',
      species: '',
      dimensions: '',
      tags: '',
      sort_order: '0',
      featured: false,
    });
    setEditingItem(null);
  };

  const handleCreate = () => {
    resetForm();
    setMode('create');
  };

  const handleEdit = (item: GalleryItem) => {
    setFormData({
      title: item.title,
      description: item.description || '',
      category: item.category,
      image_url: item.image_url || '',
      thumbnail_url: item.thumbnail_url || '',
      price: item.price?.toString() || '',
      price_display: item.price_display,
      status: item.status,
      species: item.species || '',
      dimensions: item.dimensions || '',
      tags: item.tags?.join(', ') || '',
      sort_order: item.sort_order.toString(),
      featured: item.featured,
    });
    setEditingItem(item);
    setMode('edit');
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this item?')) return;

    try {
      const response = await fetch(`/api/gallery/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        setItems(items.filter((item) => item.id !== id));
        setMessage({ type: 'success', text: 'Item deleted successfully' });
      } else {
        setMessage({ type: 'error', text: 'Failed to delete item' });
      }
    } catch {
      setMessage({ type: 'error', text: 'Network error' });
    }

    setTimeout(() => setMessage(null), 3000);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);

    const payload = {
      title: formData.title,
      description: formData.description || undefined,
      category: formData.category,
      image_url: formData.image_url || undefined,
      thumbnail_url: formData.thumbnail_url || undefined,
      price: formData.price ? parseFloat(formData.price) : undefined,
      price_display: formData.price_display,
      status: formData.status,
      species: formData.species || undefined,
      dimensions: formData.dimensions || undefined,
      tags: formData.tags ? formData.tags.split(',').map((t) => t.trim()) : undefined,
      sort_order: parseInt(formData.sort_order) || 0,
      featured: formData.featured,
    };

    try {
      const url = mode === 'edit' ? `/api/gallery/${editingItem?.id}` : '/api/gallery';
      const method = mode === 'edit' ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        setMessage({ type: 'success', text: mode === 'edit' ? 'Item updated' : 'Item created' });
        setMode('list');
        resetForm();
        loadItems();
      } else {
        const data = await response.json();
        setMessage({ type: 'error', text: data.error || 'Failed to save' });
      }
    } catch {
      setMessage({ type: 'error', text: 'Network error' });
    } finally {
      setIsSaving(false);
      setTimeout(() => setMessage(null), 3000);
    }
  };

  // Testimonial functions
  const resetTestimonialForm = () => {
    setTestimonialForm({
      name: '',
      location: '',
      service: 'general',
      text: '',
      rating: '5',
      featured: true,
      sort_order: '0',
    });
    setEditingTestimonial(null);
  };

  const handleCreateTestimonial = () => {
    resetTestimonialForm();
    setMode('create');
  };

  const handleEditTestimonial = (testimonial: Testimonial) => {
    setTestimonialForm({
      name: testimonial.name,
      location: testimonial.location || '',
      service: testimonial.service || 'general',
      text: testimonial.text,
      rating: testimonial.rating.toString(),
      featured: testimonial.featured,
      sort_order: testimonial.sort_order.toString(),
    });
    setEditingTestimonial(testimonial);
    setMode('edit');
  };

  const handleDeleteTestimonial = async (id: string) => {
    if (!confirm('Are you sure you want to delete this testimonial?')) return;

    try {
      const response = await fetch(`/api/testimonials/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        setTestimonials(testimonials.filter((t) => t.id !== id));
        setMessage({ type: 'success', text: 'Testimonial deleted successfully' });
      } else {
        setMessage({ type: 'error', text: 'Failed to delete testimonial' });
      }
    } catch {
      setMessage({ type: 'error', text: 'Network error' });
    }

    setTimeout(() => setMessage(null), 3000);
  };

  const handleTestimonialSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);

    const payload = {
      name: testimonialForm.name,
      location: testimonialForm.location || undefined,
      service: testimonialForm.service || undefined,
      text: testimonialForm.text,
      rating: parseInt(testimonialForm.rating) || 5,
      featured: testimonialForm.featured,
      sort_order: parseInt(testimonialForm.sort_order) || 0,
    };

    try {
      const url = mode === 'edit' ? `/api/testimonials/${editingTestimonial?.id}` : '/api/testimonials';
      const method = mode === 'edit' ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        setMessage({ type: 'success', text: mode === 'edit' ? 'Testimonial updated' : 'Testimonial created' });
        setMode('list');
        resetTestimonialForm();
        loadTestimonials();
      } else {
        const data = await response.json();
        setMessage({ type: 'error', text: data.error || 'Failed to save' });
      }
    } catch {
      setMessage({ type: 'error', text: 'Network error' });
    } finally {
      setIsSaving(false);
      setTimeout(() => setMessage(null), 3000);
    }
  };

  if (!isAuthenticated) {
    return null;
  }

  return (
    <section className="section-padding">
      <div className="container-custom">
        {/* Header */}
        <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
          <div>
            <span className="category-label">Admin</span>
            <h1 className="text-3xl font-bold">Dashboard</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Welcome, {user?.name || user?.email}
            </p>
          </div>
          <div className="flex gap-2">
            <Button asChild variant="outline" size="sm">
              <Link to="/">View Site</Link>
            </Button>
            <Button variant="ghost" size="sm" onClick={handleLogout}>
              <LogOut className="mr-2 h-4 w-4" />
              Logout
            </Button>
          </div>
        </div>

        {/* Message */}
        {message && (
          <div
            className={`mb-6 flex items-center gap-2 rounded-lg p-3 text-sm ${
              message.type === 'success'
                ? 'border-primary/20 bg-primary/10 text-primary'
                : 'border-destructive/20 bg-destructive/10 text-destructive'
            }`}
          >
            {message.type === 'success' ? (
              <CheckCircle className="h-4 w-4" />
            ) : (
              <AlertCircle className="h-4 w-4" />
            )}
            {message.text}
          </div>
        )}

        {/* Tabs */}
        <div className="mb-6 flex gap-2 border-b border-border pb-2">
          <Button
            variant={activeTab === 'gallery' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => { setActiveTab('gallery'); setMode('list'); }}
            className="gap-2"
          >
            <ImageIcon className="h-4 w-4" />
            Gallery
          </Button>
          <Button
            variant={activeTab === 'testimonials' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => { setActiveTab('testimonials'); setMode('list'); }}
            className="gap-2"
          >
            <MessageSquare className="h-4 w-4" />
            Testimonials
          </Button>
        </div>

        {activeTab === 'gallery' ? (
          // GALLERY TAB
          mode === 'list' ? (
            <>
              {/* Stats Cards */}
              <div className="mb-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <Card>
                  <CardContent className="flex items-center gap-4 p-4">
                    <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10 text-primary">
                      <ImageIcon className="h-6 w-6" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Total Items</p>
                      <p className="text-2xl font-bold">{items.length}</p>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="flex items-center gap-4 p-4">
                    <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-green-500/10 text-green-500">
                      <Package className="h-6 w-6" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Available</p>
                      <p className="text-2xl font-bold">
                        {items.filter((i) => i.status === 'available').length}
                      </p>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="flex items-center gap-4 p-4">
                    <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-amber-500/10 text-amber-500">
                      <DollarSign className="h-6 w-6" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Sold</p>
                      <p className="text-2xl font-bold">
                        {items.filter((i) => i.status === 'sold').length}
                      </p>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="flex items-center gap-4 p-4">
                    <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-blue-500/10 text-blue-500">
                      <Tag className="h-6 w-6" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Featured</p>
                      <p className="text-2xl font-bold">{items.filter((i) => i.featured).length}</p>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Gallery Items List */}
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <h2 className="text-xl font-semibold">Gallery Items</h2>
                  <Button onClick={handleCreate} className="gap-2">
                    <Plus className="h-4 w-4" />
                    Add Item
                  </Button>
                </CardHeader>
                <CardContent>
                  {isLoadingItems ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                    </div>
                  ) : items.length === 0 ? (
                    <div className="py-12 text-center text-muted-foreground">
                      <ImageIcon className="mx-auto h-12 w-12 opacity-50" />
                      <p className="mt-4">No gallery items yet</p>
                      <Button onClick={handleCreate} className="mt-4 gap-2">
                        <Plus className="h-4 w-4" />
                        Add Your First Item
                      </Button>
                    </div>
                  ) : (
                    <div className="divide-y divide-border">
                      {items.map((item) => (
                        <div
                          key={item.id}
                          className="flex items-center gap-4 py-4 first:pt-0 last:pb-0"
                        >
                          {/* Thumbnail */}
                          <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-lg bg-muted">
                            {item.thumbnail_url || item.image_url ? (
                              <img
                                src={item.thumbnail_url || item.image_url || ''}
                                alt={item.title}
                                className="h-full w-full rounded-lg object-cover"
                              />
                            ) : (
                              <ImageIcon className="h-6 w-6 text-muted-foreground" />
                            )}
                          </div>

                          {/* Info */}
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <h3 className="font-semibold truncate">{item.title}</h3>
                              {item.featured && (
                                <span className="rounded bg-primary/10 px-2 py-0.5 text-xs text-primary">
                                  Featured
                                </span>
                              )}
                            </div>
                            <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                              <span className="capitalize">{item.category}</span>
                              <span>•</span>
                              <span
                                className={`capitalize ${
                                  item.status === 'available'
                                    ? 'text-green-500'
                                    : item.status === 'sold'
                                    ? 'text-amber-500'
                                    : item.status === 'hidden'
                                    ? 'text-muted-foreground'
                                    : 'text-blue-500'
                                }`}
                              >
                                {item.status}
                              </span>
                            </div>
                          </div>

                          {/* Actions */}
                          <div className="flex shrink-0 gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleEdit(item)}
                              title="Edit"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDelete(item.id)}
                              title="Delete"
                              className="text-destructive hover:text-destructive"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          ) : (
            /* Gallery Create/Edit Form */
            <Card>
              <CardHeader>
                <h2 className="text-xl font-semibold">
                  {mode === 'create' ? 'Add New Item' : 'Edit Item'}
                </h2>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid gap-6 md:grid-cols-2">
                    {/* Title */}
                    <div className="space-y-2">
                      <Label htmlFor="title">Title *</Label>
                      <Input
                        id="title"
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        required
                      />
                    </div>

                    {/* Category */}
                    <div className="space-y-2">
                      <Label htmlFor="category">Category *</Label>
                      <select
                        id="category"
                        value={formData.category}
                        onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      >
                        {categoryOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Status */}
                    <div className="space-y-2">
                      <Label htmlFor="status">Status</Label>
                      <select
                        id="status"
                        value={formData.status}
                        onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      >
                        {statusOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Price Display */}
                    <div className="space-y-2">
                      <Label htmlFor="price_display">Price Display</Label>
                      <select
                        id="price_display"
                        value={formData.price_display}
                        onChange={(e) => setFormData({ ...formData, price_display: e.target.value })}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      >
                        {priceDisplayOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Price */}
                    <div className="space-y-2">
                      <Label htmlFor="price">Price ($)</Label>
                      <Input
                        id="price"
                        type="number"
                        step="0.01"
                        value={formData.price}
                        onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                        placeholder="Leave empty for price upon request"
                      />
                    </div>

                    {/* Species */}
                    <div className="space-y-2">
                      <Label htmlFor="species">Species</Label>
                      <Input
                        id="species"
                        value={formData.species}
                        onChange={(e) => setFormData({ ...formData, species: e.target.value })}
                        placeholder="e.g., Kookaburra"
                      />
                    </div>

                    {/* Dimensions */}
                    <div className="space-y-2">
                      <Label htmlFor="dimensions">Dimensions</Label>
                      <Input
                        id="dimensions"
                        value={formData.dimensions}
                        onChange={(e) => setFormData({ ...formData, dimensions: e.target.value })}
                        placeholder="e.g., 30cm x 20cm x 15cm"
                      />
                    </div>

                    {/* Sort Order */}
                    <div className="space-y-2">
                      <Label htmlFor="sort_order">Sort Order</Label>
                      <Input
                        id="sort_order"
                        type="number"
                        value={formData.sort_order}
                        onChange={(e) => setFormData({ ...formData, sort_order: e.target.value })}
                      />
                    </div>
                  </div>

                  {/* Description */}
                  <div className="space-y-2">
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows={3}
                    />
                  </div>

                  {/* Images Section */}
                  <div className="space-y-6">
                    <h3 className="text-lg font-medium">Images</h3>

                    <div className="grid gap-6 md:grid-cols-2">
                      {/* Main Image */}
                      <div className="space-y-4">
                        <Label>Main Image</Label>
                        {formData.image_url && (
                          <div className="relative">
                            <img
                              src={formData.image_url}
                              alt="Main preview"
                              className="h-40 w-full rounded-lg border border-border object-cover"
                            />
                            <Button
                              type="button"
                              variant="destructive"
                              size="icon"
                              className="absolute -right-2 -top-2 h-6 w-6"
                              onClick={() => setFormData({ ...formData, image_url: '' })}
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                        )}
                        <div className="flex gap-2">
                          <label className="flex-1">
                            <div className="flex cursor-pointer items-center justify-center gap-2 rounded-md border border-dashed border-border bg-muted/50 px-4 py-3 text-sm hover:bg-muted">
                              <Upload className="h-4 w-4" />
                              Upload Image
                            </div>
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) {
                                  const reader = new FileReader();
                                  reader.onload = (event) => {
                                    setFormData({ ...formData, image_url: event.target?.result as string });
                                  };
                                  reader.readAsDataURL(file);
                                }
                              }}
                            />
                          </label>
                        </div>
                        <div className="space-y-1">
                          <span className="text-xs text-muted-foreground">Or enter URL:</span>
                          <Input
                            type="url"
                            value={formData.image_url.startsWith('data:') ? '' : formData.image_url}
                            onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                            placeholder="https://..."
                          />
                        </div>
                      </div>

                      {/* Thumbnail */}
                      <div className="space-y-4">
                        <Label>Thumbnail</Label>
                        {formData.thumbnail_url && (
                          <div className="relative">
                            <img
                              src={formData.thumbnail_url}
                              alt="Thumbnail preview"
                              className="h-40 w-full rounded-lg border border-border object-cover"
                            />
                            <Button
                              type="button"
                              variant="destructive"
                              size="icon"
                              className="absolute -right-2 -top-2 h-6 w-6"
                              onClick={() => setFormData({ ...formData, thumbnail_url: '' })}
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                        )}
                        <div className="flex gap-2">
                          <label className="flex-1">
                            <div className="flex cursor-pointer items-center justify-center gap-2 rounded-md border border-dashed border-border bg-muted/50 px-4 py-3 text-sm hover:bg-muted">
                              <Upload className="h-4 w-4" />
                              Upload Thumbnail
                            </div>
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) {
                                  const reader = new FileReader();
                                  reader.onload = (event) => {
                                    setFormData({ ...formData, thumbnail_url: event.target?.result as string });
                                  };
                                  reader.readAsDataURL(file);
                                }
                              }}
                            />
                          </label>
                        </div>
                        <div className="space-y-1">
                          <span className="text-xs text-muted-foreground">Or enter URL:</span>
                          <Input
                            type="url"
                            value={formData.thumbnail_url.startsWith('data:') ? '' : formData.thumbnail_url}
                            onChange={(e) => setFormData({ ...formData, thumbnail_url: e.target.value })}
                            placeholder="https://..."
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Tags */}
                  <div className="space-y-2">
                    <Label htmlFor="tags">Tags (comma separated)</Label>
                    <Input
                      id="tags"
                      value={formData.tags}
                      onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                      placeholder="native, bird, colorful"
                    />
                  </div>

                  {/* Featured checkbox */}
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="featured"
                      checked={formData.featured}
                      onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                      className="h-4 w-4 rounded border-input"
                    />
                    <Label htmlFor="featured" className="cursor-pointer">
                      Featured item (show on homepage)
                    </Label>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-4">
                    <Button type="submit" disabled={isSaving}>
                      {isSaving ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Saving...
                        </>
                      ) : mode === 'create' ? (
                        'Create Item'
                      ) : (
                        'Update Item'
                      )}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setMode('list');
                        resetForm();
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )
        ) : (
          // TESTIMONIALS TAB
          mode === 'list' ? (
            <>
              {/* Stats */}
              <div className="mb-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                <Card>
                  <CardContent className="flex items-center gap-4 p-4">
                    <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10 text-primary">
                      <MessageSquare className="h-6 w-6" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Total Reviews</p>
                      <p className="text-2xl font-bold">{testimonials.length}</p>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="flex items-center gap-4 p-4">
                    <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-green-500/10 text-green-500">
                      <Star className="h-6 w-6" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Featured</p>
                      <p className="text-2xl font-bold">{testimonials.filter((t) => t.featured).length}</p>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="flex items-center gap-4 p-4">
                    <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-amber-500/10 text-amber-500">
                      <Star className="h-6 w-6 fill-amber-500" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Avg Rating</p>
                      <p className="text-2xl font-bold">
                        {testimonials.length > 0
                          ? (testimonials.reduce((acc, t) => acc + t.rating, 0) / testimonials.length).toFixed(1)
                          : '-'}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Testimonials List */}
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <h2 className="text-xl font-semibold">Testimonials</h2>
                  <Button onClick={handleCreateTestimonial} className="gap-2">
                    <Plus className="h-4 w-4" />
                    Add Testimonial
                  </Button>
                </CardHeader>
                <CardContent>
                  {isLoadingTestimonials ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                    </div>
                  ) : testimonials.length === 0 ? (
                    <div className="py-12 text-center text-muted-foreground">
                      <MessageSquare className="mx-auto h-12 w-12 opacity-50" />
                      <p className="mt-4">No testimonials yet</p>
                      <p className="mt-1 text-sm">Add customer reviews to display on your homepage</p>
                      <Button onClick={handleCreateTestimonial} className="mt-4 gap-2">
                        <Plus className="h-4 w-4" />
                        Add Your First Testimonial
                      </Button>
                    </div>
                  ) : (
                    <div className="divide-y divide-border">
                      {testimonials.map((testimonial) => (
                        <div
                          key={testimonial.id}
                          className="flex items-start gap-4 py-4 first:pt-0 last:pb-0"
                        >
                          {/* Info */}
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h3 className="font-semibold">{testimonial.name}</h3>
                              {testimonial.location && (
                                <span className="text-sm text-muted-foreground">
                                  • {testimonial.location}
                                </span>
                              )}
                              {testimonial.featured && (
                                <span className="rounded bg-primary/10 px-2 py-0.5 text-xs text-primary">
                                  Featured
                                </span>
                              )}
                            </div>
                            <div className="mt-1 flex gap-0.5">
                              {[...Array(5)].map((_, i) => (
                                <Star
                                  key={i}
                                  className={`h-3 w-3 ${
                                    i < testimonial.rating
                                      ? 'fill-amber-400 text-amber-400'
                                      : 'text-muted-foreground'
                                  }`}
                                />
                              ))}
                            </div>
                            <p className="mt-2 text-sm text-muted-foreground line-clamp-2">
                              "{testimonial.text}"
                            </p>
                            {testimonial.service && (
                              <span className="mt-2 inline-block rounded-full bg-muted px-2 py-0.5 text-xs capitalize">
                                {testimonial.service}
                              </span>
                            )}
                          </div>

                          {/* Actions */}
                          <div className="flex shrink-0 gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleEditTestimonial(testimonial)}
                              title="Edit"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteTestimonial(testimonial.id)}
                              title="Delete"
                              className="text-destructive hover:text-destructive"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          ) : (
            /* Testimonial Create/Edit Form */
            <Card>
              <CardHeader>
                <h2 className="text-xl font-semibold">
                  {mode === 'create' ? 'Add New Testimonial' : 'Edit Testimonial'}
                </h2>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleTestimonialSubmit} className="space-y-6">
                  <div className="grid gap-6 md:grid-cols-2">
                    {/* Name */}
                    <div className="space-y-2">
                      <Label htmlFor="t-name">Customer Name *</Label>
                      <Input
                        id="t-name"
                        value={testimonialForm.name}
                        onChange={(e) => setTestimonialForm({ ...testimonialForm, name: e.target.value })}
                        required
                        placeholder="e.g., Sarah M."
                      />
                    </div>

                    {/* Location */}
                    <div className="space-y-2">
                      <Label htmlFor="t-location">Location</Label>
                      <Input
                        id="t-location"
                        value={testimonialForm.location}
                        onChange={(e) => setTestimonialForm({ ...testimonialForm, location: e.target.value })}
                        placeholder="e.g., Hobart"
                      />
                    </div>

                    {/* Service */}
                    <div className="space-y-2">
                      <Label htmlFor="t-service">Service</Label>
                      <select
                        id="t-service"
                        value={testimonialForm.service}
                        onChange={(e) => setTestimonialForm({ ...testimonialForm, service: e.target.value })}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      >
                        {serviceOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Rating */}
                    <div className="space-y-2">
                      <Label htmlFor="t-rating">Rating</Label>
                      <select
                        id="t-rating"
                        value={testimonialForm.rating}
                        onChange={(e) => setTestimonialForm({ ...testimonialForm, rating: e.target.value })}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      >
                        <option value="5">5 Stars</option>
                        <option value="4">4 Stars</option>
                        <option value="3">3 Stars</option>
                        <option value="2">2 Stars</option>
                        <option value="1">1 Star</option>
                      </select>
                    </div>

                    {/* Sort Order */}
                    <div className="space-y-2">
                      <Label htmlFor="t-sort">Sort Order</Label>
                      <Input
                        id="t-sort"
                        type="number"
                        value={testimonialForm.sort_order}
                        onChange={(e) => setTestimonialForm({ ...testimonialForm, sort_order: e.target.value })}
                      />
                    </div>
                  </div>

                  {/* Text */}
                  <div className="space-y-2">
                    <Label htmlFor="t-text">Testimonial Text *</Label>
                    <Textarea
                      id="t-text"
                      value={testimonialForm.text}
                      onChange={(e) => setTestimonialForm({ ...testimonialForm, text: e.target.value })}
                      rows={4}
                      required
                      placeholder="Write the customer's testimonial here..."
                    />
                  </div>

                  {/* Featured checkbox */}
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="t-featured"
                      checked={testimonialForm.featured}
                      onChange={(e) => setTestimonialForm({ ...testimonialForm, featured: e.target.checked })}
                      className="h-4 w-4 rounded border-input"
                    />
                    <Label htmlFor="t-featured" className="cursor-pointer">
                      Show on homepage
                    </Label>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-4">
                    <Button type="submit" disabled={isSaving}>
                      {isSaving ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Saving...
                        </>
                      ) : mode === 'create' ? (
                        'Create Testimonial'
                      ) : (
                        'Update Testimonial'
                      )}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setMode('list');
                        resetTestimonialForm();
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )
        )}
      </div>
    </section>
  );
}
